<template>
  <template v-if="view">
    <Alert :variant="view.type" :square="square">
      {{ view.text }}
      <div v-if="billingPortal.errorKey.value" class="mt-1 text-xs font-medium">
        {{ t(billingPortal.errorKey.value) }}
      </div>

      <template v-if="view.upgrade || view.portal" #actions>
        <!-- upgrading is the main action; the portal is a secondary one -->
        <Button
          v-if="view.upgrade"
          variant="primary"
          size="sm"
          type="button"
          @click="modals?.openUpgrade()"
        >
          {{ view.upgrade }}
        </Button>
        <Button
          v-if="view.portal"
          variant="secondary"
          size="sm"
          type="button"
          :disabled="billingPortal.opening.value"
          :aria-busy="billingPortal.opening.value"
          @click="billingPortal.open"
        >
          <MdiLoading
            v-if="billingPortal.opening.value"
            class="animate-spin"
            aria-hidden="true"
          />
          {{ view.portal }}
        </Button>
      </template>
    </Alert>
  </template>

  <TrialModals
    ref="modals"
    :org-id="orgId"
    :subscription="subscription"
    @upgraded="onUpgraded"
    @cancelled="emit('changed')"
    @stale="emit('changed')"
  />

  <Toast
    v-model:model-value="showToast"
    :message="toastMessage"
    position="bottom-right"
    :duration="4000"
  />
</template>

<script setup lang="ts">
import { computed, onUnmounted, ref, toRef, watch } from "vue";
import { useI18n } from "vue-i18n";
import { getTrialStatus, type Org } from "tf-api-client";
import { useBillingPortal } from "../../composables/use-billing-portal";
import {
  contractDescription,
  isSelfServiceBilling,
  TRIAL_ENDED_REFETCH_INTERVAL_MS,
} from "../../config/licensing";
import i18nUtil from "../../utils/i18n";
import Alert from "../Base/Alert.vue";
import Button from "../Base/Button.vue";
import Toast from "../Base/Toast.vue";
import TrialModals from "./TrialModals.vue";
import MdiLoading from "~icons/mdi/loading";

// Trial countdown with upgrade (and billing portal) actions; with manual
// billing only the countdown: sales agree on the subscription. The parent
// decides whether it applies: org admins of a Manager/Standard organization
// only, never a Managed one (its subscription is its Manager's trial).
const props = defineProps<{
  orgId: string | null;
  subscription: Org.Subscription | null;
  // square corners: the app shell's full-width strip
  square?: boolean;
}>();

const emit = defineEmits<{
  // the subscription changed (upgrade, cancel) or should be fetched again
  changed: [];
}>();

const { t } = useI18n();
const billingPortal = useBillingPortal(toRef(props, "orgId"));
const modals = ref<InstanceType<typeof TrialModals> | null>(null);

// Against the browser clock, like the lifecycle banners.
const status = computed(() =>
  props.subscription ? getTrialStatus(props.subscription) : null,
);

type View = {
  type: "info" | "warning";
  text: string;
  upgrade?: string;
  portal?: string;
};

const view = computed<View | null>(() => {
  const s = status.value;
  if (!s) return null;

  // Manual billing: no upgrade, card or portal (those endpoints answer 404);
  // sales get in touch before the end. Cancel stays on the subscription page.
  if (!isSelfServiceBilling(props.subscription)) {
    switch (s.state) {
      case "running":
        return {
          type: "info",
          text: t("licensing.trial.bannerRunningManual", {
            date: i18nUtil.formatLongDate(s.endsOn),
          }),
        };
      case "cancelled":
        return {
          type: "warning",
          text: t("licensing.trial.bannerCancelled", {
            date: i18nUtil.formatLongDate(s.endsOn),
          }),
        };
      case "ended":
        return { type: "info", text: t("licensing.trial.bannerEndedManual") };
      default:
        return null;
    }
  }

  switch (s.state) {
    case "running": {
      const date = i18nUtil.formatLongDate(s.endsOn);
      // a card is on file: at its end the trial converts for the contract
      // chosen at the card step
      const sub = props.subscription;
      const contract = sub
        ? contractDescription(t, sub.contractTermMonths, sub.billingInterval)
        : "";
      return s.convertsAutomatically
        ? {
            type: "info",
            text: contract
              ? t("licensing.trial.bannerRunningContract", {
                  daysLeft: t("licensing.billing.daysLeft", s.daysLeft),
                  date,
                  contract,
                })
              : t("licensing.trial.bannerRunning", {
                  daysLeft: t("licensing.billing.daysLeft", s.daysLeft),
                  date,
                }),
            upgrade: t("licensing.trial.upgradeNow"),
            portal: t("licensing.trial.managePaymentMethod"),
          }
        : // a trial created by support: no card, it lapses unless upgraded
          {
            type: "warning",
            text: t("licensing.trial.bannerRunningNoCard", { date }),
            upgrade: t("licensing.trial.upgradeNow"),
          };
    }

    case "cancelled":
      return {
        type: "warning",
        text: t("licensing.trial.bannerCancelled", {
          date: i18nUtil.formatLongDate(s.endsOn),
        }),
        // the upgrade dialog goes through the paid Checkout when the trial
        // has no Stripe subscription any more
        upgrade: t("licensing.trial.subscribeNow"),
      };

    case "ended":
      return { type: "info", text: t("licensing.trial.bannerEnded") };

    default:
      // `awaiting_card` belongs to the card step, never a normal session
      return null;
  }
});

// Ended: the daily run converts (or lapses) it. Check back now and then
// until the subscription says what happened.
let endedTimer: ReturnType<typeof setInterval> | undefined;
watch(
  () => status.value?.state === "ended",
  (ended) => {
    clearInterval(endedTimer);
    if (ended) {
      endedTimer = setInterval(
        () => emit("changed"),
        TRIAL_ENDED_REFETCH_INTERVAL_MS,
      );
    }
  },
  { immediate: true },
);
onUnmounted(() => clearInterval(endedTimer));

const showToast = ref(false);
const toastMessage = ref("");

const onUpgraded = () => {
  toastMessage.value = t("licensing.trial.upgraded");
  showToast.value = true;
  emit("changed");
};

// the subscription page's own "Upgrade" and "Cancel trial" actions reuse
// these dialogs
defineExpose({
  openUpgrade: () => modals.value?.openUpgrade(),
  openCancel: () => modals.value?.openCancel(),
});
</script>
